import React, {
  createContext,
  useContext,
  PropsWithChildren,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useRouter } from "next/router";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { useUserVenueSession } from "@shared/modules/venue/hooks/useUserVenueSession";
import useUserVenueEmploymentsService from "@app-contexts/dashboard/features/venues/data/services/venue-employments/useUserVenueEmploymentsService";

export interface DashboardAuthState {
  isReady: boolean;
  isLoading: boolean;
}

const DashboardAuthContext = createContext<DashboardAuthState | null>(null);

/**
 * Provider that checks if the user has employments to allow them to access the dashboard.
 * Handles redirecting user based on access.
 */
export const DashboardAuthProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const router = useRouter();
  // Track if we're currently redirecting to prevent duplicate redirects
  const isRedirecting = useRef(false);

  // Get user authentication state
  const { isLoggedIn, isLoading: isLoadingProfile } = useUserSessionAuthState();

  const { isLoading: isLoadingVenue } = useUserVenueSession();
  const { useList } = useUserVenueEmploymentsService();
  const { data: employmentsData, isLoading: isLoadingEmployments } = useList();

  const isDashboardRoute = useMemo(
    () => router.pathname.startsWith("/manage"),
    [router.pathname]
  );

  const hasEmployments = useMemo(
    () => Boolean(employmentsData && employmentsData.length > 0),
    [employmentsData]
  );

  const redirectToLogin = useCallback(() => {
    if (isRedirecting.current) return; // Prevent duplicate redirects
    isRedirecting.current = true;
    router.push(`/accounts/login/?next=${router.asPath}`);
  }, [router]);

  const redirectToHome = useCallback(() => {
    if (isRedirecting.current) return; // Prevent duplicate redirects
    isRedirecting.current = true;
    router.push("/");
  }, [router]);

  // Reset redirect flag when route changes
  useEffect(() => {
    const handleRouteChange = () => {
      isRedirecting.current = false;
    };

    router.events.on("routeChangeComplete", handleRouteChange);
    router.events.on("routeChangeError", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      router.events.off("routeChangeError", handleRouteChange);
    };
  }, [router]);

  // Handle authentication redirects
  useEffect(() => {
    if (!isDashboardRoute || isRedirecting.current) return;

    if (isLoggedIn === false) {
      redirectToLogin();
      return;
    }

    const hasEmploymentData =
      isLoggedIn && !isLoadingEmployments && employmentsData !== undefined;

    if (hasEmploymentData && !hasEmployments) {
      redirectToHome();
    }
  }, [
    isDashboardRoute,
    isLoggedIn,
    isLoadingEmployments,
    employmentsData,
    hasEmployments,
    redirectToLogin,
    redirectToHome,
  ]);

  const isLoading = isLoadingProfile || isLoadingEmployments || isLoadingVenue;
  const isReady = Boolean(!isLoading && hasEmployments);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo<DashboardAuthState>(
    () => ({
      isReady,
      isLoading,
    }),
    [isReady, isLoading]
  );

  return (
    <DashboardAuthContext.Provider value={value}>
      {children}
    </DashboardAuthContext.Provider>
  );
};

/**
 * Hook to access dashboard ready state.
 * Must be used within a DashboardAuthProvider.
 */
export const useDashboardAuth = (): DashboardAuthState => {
  const context = useContext(DashboardAuthContext);

  if (context === null) {
    throw new Error(
      "useDashboardAuth must be used within a DashboardAuthProvider"
    );
  }

  return context;
};
